import { useEffect, useState } from 'react';
import { LoadingSpinner } from '@/shared/components/Loading';
import { formatCents, useAdjustUserBalance, useFlagUser, useOverrideUserTier, useSearchReferralUsers, SearchUserResult } from '@/services/referrals';

/**
 * Search-driven user management for the referral program (proposal §1.6 §
 * "User Management"): search by email or referral code, manually override
 * tier with reason, disable referral capability for flagged accounts,
 * adjust balance.
 */
const MIN_SEARCH_LENGTH = 3;

const UserManagementTab = () => {
    const [query, setQuery] = useState('');
    const [debounced, setDebounced] = useState('');
    // Honour the "at least 3 characters" hint — don't fire the API on 1–2 chars.
    const effectiveQuery = debounced.length >= MIN_SEARCH_LENGTH ? debounced : '';
    const { data, isLoading } = useSearchReferralUsers(effectiveQuery);
    const [actingOn, setActingOn] = useState<{ user: SearchUserResult; mode: 'tier' | 'flag' | 'balance' } | null>(null);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(query.trim()), 300);
        return () => clearTimeout(t);
    }, [query]);

    return (
        <div>
            <p className="text-muted">Search by email (prefix) or referral code (exact). Results are capped at 25.</p>
            <div className="d-flex gap-2 mb-3">
                <input
                    type="search"
                    className="form-control"
                    placeholder="email@example.com or HARM-A3X9K2"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />
            </div>

            {!effectiveQuery && <div className="text-muted small">Enter at least 3 characters to search.</div>}

            {effectiveQuery && isLoading && <LoadingSpinner />}

            {effectiveQuery && !isLoading && (
                <div className="table-responsive">
                    <table className="table table-hover align-middle">
                        <thead>
                            <tr>
                                <th>User</th>
                                <th>Code</th>
                                <th>Tier</th>
                                <th className="text-end">Qualified</th>
                                <th className="text-end">Total</th>
                                <th>Status</th>
                                <th className="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data?.data?.users || []).map((u) => (
                                <tr key={u.id}>
                                    <td>
                                        <div>{u.name || u.email}</div>
                                        <div className="text-muted small font-monospace">{u.id}</div>
                                    </td>
                                    <td className="font-monospace">{u.referralCode || '—'}</td>
                                    <td>{u.referralTier ? `Tier ${u.referralTier}` : '—'}</td>
                                    <td className="text-end">{u.qualifiedReferralsCount || 0}</td>
                                    <td className="text-end">{u.totalReferralsCount || 0}</td>
                                    <td>
                                        {u.referralFlagged ? (
                                            <span className="badge text-bg-danger">Flagged</span>
                                        ) : (
                                            <span className="badge text-bg-success">Active</span>
                                        )}
                                    </td>
                                    <td className="text-end">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary me-1"
                                            onClick={() => setActingOn({ user: u, mode: 'tier' })}
                                        >
                                            Tier
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary me-1"
                                            onClick={() => setActingOn({ user: u, mode: 'flag' })}
                                        >
                                            {u.referralFlagged ? 'Unflag' : 'Flag'}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => setActingOn({ user: u, mode: 'balance' })}
                                        >
                                            Balance
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {(data?.data?.users || []).length === 0 && (
                                <tr>
                                    <td colSpan={7} className="text-center text-muted py-4">
                                        No matches.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {actingOn?.mode === 'tier' && <TierOverrideModal user={actingOn.user} onClose={() => setActingOn(null)} />}
            {actingOn?.mode === 'flag' && <FlagModal user={actingOn.user} onClose={() => setActingOn(null)} />}
            {actingOn?.mode === 'balance' && <BalanceModal user={actingOn.user} onClose={() => setActingOn(null)} />}
        </div>
    );
};

export default UserManagementTab;

const TierOverrideModal = ({ user, onClose }: { user: SearchUserResult; onClose: () => void }) => {
    const [tier, setTier] = useState<1 | 2 | 3 | 'auto'>(user.referralTier || 'auto');
    const [reason, setReason] = useState('');
    const mutation = useOverrideUserTier();
    const [error, setError] = useState<string | null>(null);
    const submit = async () => {
        setError(null);
        try {
            // callApi resolves { status:'error' } rather than throwing — inspect it
            // so a failed override doesn't silently close as success.
            const resp = await mutation.mutateAsync({ userId: user.id, tier: tier === 'auto' ? null : tier, reason });
            if (resp.status === 'error') {
                setError(resp.error?.message ?? 'Failed to override tier.');
                return;
            }
            onClose();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to override tier.');
        }
    };
    return (
        <ModalShell title={`Override tier — ${user.email}`} onClose={onClose}>
            {error && <div className="alert alert-danger py-2">{error}</div>}
            <div className="mb-3">
                <label className="form-label">New tier</label>
                <select
                    className="form-select"
                    value={tier}
                    onChange={(e) => setTier(e.target.value === 'auto' ? 'auto' : (Number(e.target.value) as 1 | 2 | 3))}
                >
                    <option value="auto">Auto (clear override)</option>
                    <option value={1}>Tier 1 · Practitioner (10%)</option>
                    <option value={2}>Tier 2 · Ambassador (20%)</option>
                    <option value={3}>Tier 3 · Circle (30%)</option>
                </select>
            </div>
            <div className="mb-3">
                <label className="form-label">Reason (optional)</label>
                <textarea className="form-control" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
            <div className="d-flex justify-content-end gap-2">
                <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                    Cancel
                </button>
                <button type="button" className="btn btn-primary" disabled={mutation.isPending} onClick={submit}>
                    {mutation.isPending ? 'Saving…' : 'Save'}
                </button>
            </div>
        </ModalShell>
    );
};

const FlagModal = ({ user, onClose }: { user: SearchUserResult; onClose: () => void }) => {
    const flagged = !user.referralFlagged;
    const [reason, setReason] = useState('');
    const mutation = useFlagUser();
    const [error, setError] = useState<string | null>(null);
    const submit = async () => {
        setError(null);
        try {
            const resp = await mutation.mutateAsync({ userId: user.id, flagged, reason });
            if (resp.status === 'error') {
                setError(resp.error?.message ?? 'Failed to update flag.');
                return;
            }
            onClose();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to update flag.');
        }
    };
    return (
        <ModalShell title={`${flagged ? 'Flag' : 'Unflag'} — ${user.email}`} onClose={onClose}>
            {error && <div className="alert alert-danger py-2">{error}</div>}
            <div className="mb-3">
                <label className="form-label">Reason</label>
                <textarea className="form-control" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
            <div className="d-flex justify-content-end gap-2">
                <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                    Cancel
                </button>
                <button type="button" className={flagged ? 'btn btn-danger' : 'btn btn-success'} disabled={mutation.isPending} onClick={submit}>
                    {flagged ? 'Flag user' : 'Unflag user'}
                </button>
            </div>
        </ModalShell>
    );
};

const BalanceModal = ({ user, onClose }: { user: SearchUserResult; onClose: () => void }) => {
    const [delta, setDelta] = useState('0');
    const [reason, setReason] = useState('');
    const mutation = useAdjustUserBalance();
    const [error, setError] = useState<string | null>(null);
    const submit = async () => {
        const deltaDollars = parseFloat(delta || '0');
        if (!Number.isFinite(deltaDollars)) return;
        if (deltaDollars === 0) {
            setError('Enter a non-zero amount.');
            return;
        }
        // Financial ledger write — confirm the direction + amount so a typo/mis-click
        // can't silently move money on a real user's balance.
        const verb = deltaDollars > 0 ? 'ADD' : 'DEDUCT';
        const preposition = deltaDollars > 0 ? 'to' : 'from';
        if (
            !window.confirm(
                `${verb} $${Math.abs(deltaDollars).toFixed(2)} ${preposition} ${user.email}'s referral balance? This writes to the payouts ledger.`,
            )
        ) {
            return;
        }
        setError(null);
        try {
            // Touches the payouts ledger — a failed adjustment must NOT close as
            // success. callApi resolves { status:'error' } rather than throwing.
            const resp = await mutation.mutateAsync({ userId: user.id, deltaCents: Math.round(deltaDollars * 100), reason });
            if (resp.status === 'error') {
                setError(resp.error?.message ?? 'Failed to adjust balance.');
                return;
            }
            onClose();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to adjust balance.');
        }
    };
    return (
        <ModalShell title={`Adjust balance — ${user.email}`} onClose={onClose}>
            {error && <div className="alert alert-danger py-2">{error}</div>}
            <p className="text-muted small">Use a negative number to deduct.</p>
            <div className="mb-3">
                <label className="form-label">Delta (USD)</label>
                <input type="number" step="0.01" className="form-control" value={delta} onChange={(e) => setDelta(e.target.value)} />
            </div>
            <div className="mb-3">
                <label className="form-label">Reason (required)</label>
                <textarea className="form-control" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
            <div className="d-flex justify-content-end gap-2">
                <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                    Cancel
                </button>
                <button type="button" className="btn btn-primary" disabled={mutation.isPending || reason.trim().length === 0} onClick={submit}>
                    {mutation.isPending
                        ? 'Saving…'
                        : `Adjust ${delta && Number(delta) !== 0 ? `(${formatCents(Math.round(Number(delta) * 100))})` : ''}`}
                </button>
            </div>
        </ModalShell>
    );
};

const ModalShell = ({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) => (
    <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1} role="dialog" onClick={onClose}>
        <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
                <div className="modal-header">
                    <h5 className="modal-title">{title}</h5>
                    <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
                </div>
                <div className="modal-body">{children}</div>
            </div>
        </div>
    </div>
);
